import { filemakerService } from './filemakerService.js';

/**
 * Servicio para gestión de suscripciones usando FileMaker
 */
export class SubscriptionsService {
  /**
   * Obtener todas las suscripciones con filtros opcionales
   */
  static async getSubscriptions(filters = {}) {
    try {
      const response = await filemakerService.findRecords('subscription_states', {
        query: filters.search ? [
          {
            "cliente_nombre": `*${filters.search}*`,
            "cliente_email": `*${filters.search}*`
          }
        ] : undefined,
        sort: [{ fieldName: "fecha_creacion", sortOrder: "descend" }]
      });
      
      return response.data.map(record => ({
        id: record.fieldData.id,
        clientName: record.fieldData.cliente_nombre,
        clientEmail: record.fieldData.cliente_email,
        planName: record.fieldData.plan_nombre,
        status: record.fieldData.estado,
        nextPayment: record.fieldData.proximo_pago,
        amount: parseFloat(record.fieldData.monto_uf) || 0,
        startDate: record.fieldData.fecha_inicio,
        paymentMethod: record.fieldData.medio_pago,
        periodicidad: record.fieldData.periodicidad,
        created_at: record.fieldData.fecha_creacion,
        updated_at: record.fieldData.fecha_modificacion
      }));
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      throw error;
    }
  }

  /**
   * Obtener una suscripción por ID
   */
  static async getSubscriptionById(id) {
    try {
      const response = await filemakerService.getRecord('subscription_states', id);
      
      return {
        id: response.fieldData.id,
        clientName: response.fieldData.cliente_nombre,
        clientEmail: response.fieldData.cliente_email,
        planName: response.fieldData.plan_nombre,
        status: response.fieldData.estado,
        nextPayment: response.fieldData.proximo_pago,
        amount: parseFloat(response.fieldData.monto_uf) || 0,
        startDate: response.fieldData.fecha_inicio,
        paymentMethod: response.fieldData.medio_pago,
        periodicidad: response.fieldData.periodicidad,
        created_at: response.fieldData.fecha_creacion,
        updated_at: response.fieldData.fecha_modificacion
      };
    } catch (error) {
      console.error('Error fetching subscription:', error);
      throw error;
    }
  }

  /**
   * Crear una nueva suscripción
   */
  static async createSubscription(subscriptionData) {
    try {
      const fieldData = {
        cliente_nombre: subscriptionData.clientName,
        cliente_email: subscriptionData.clientEmail,
        plan_nombre: subscriptionData.planName,
        estado: subscriptionData.status || 'active',
        proximo_pago: subscriptionData.nextPayment,
        monto_uf: subscriptionData.amount,
        fecha_inicio: subscriptionData.startDate,
        medio_pago: subscriptionData.paymentMethod,
        periodicidad: subscriptionData.periodicidad
      };
      
      const response = await filemakerService.createRecord('subscription_states', fieldData);
      return response;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  /**
   * Actualizar una suscripción
   */
  static async updateSubscription(id, updates) {
    try {
      const fieldData = {};
      if (updates.clientName) fieldData.cliente_nombre = updates.clientName;
      if (updates.clientEmail) fieldData.cliente_email = updates.clientEmail;
      if (updates.planName) fieldData.plan_nombre = updates.planName;
      if (updates.status) fieldData.estado = updates.status;
      if (updates.nextPayment) fieldData.proximo_pago = updates.nextPayment;
      if (updates.amount) fieldData.monto_uf = updates.amount;
      if (updates.startDate) fieldData.fecha_inicio = updates.startDate;
      if (updates.paymentMethod) fieldData.medio_pago = updates.paymentMethod;
      if (updates.periodicidad) fieldData.periodicidad = updates.periodicidad;
      
      const response = await filemakerService.updateRecord('subscription_states', id, fieldData);
      return response;
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }

  /**
   * Cancelar una suscripción
   */
  static async cancelSubscription(id, reason = '') {
    try {
      const fieldData = {
        estado: 'cancelled',
        fecha_cancelacion: new Date().toISOString(),
        razon_cancelacion: reason
      };
      
      const response = await filemakerService.updateRecord('subscription_states', id, fieldData);
      return response;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  }

  /**
   * Renovar una suscripción
   */
  static async renewSubscription(id, newExpiryDate) {
    try {
      const fieldData = {
        estado: 'active',
        fecha_expiracion: newExpiryDate,
        fecha_renovacion: new Date().toISOString()
      };
      
      const response = await filemakerService.updateRecord('subscription_states', id, fieldData);
      return response;
    } catch (error) {
      console.error('Error renewing subscription:', error);
      throw error;
    }
  }

  /**
   * Obtener suscripciones que expiran pronto
   */
  static async getExpiringSubscriptions(daysAhead = 7) {
    try {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + daysAhead);
      
      const response = await filemakerService.findRecords('subscription_states', {
        query: [{
          "estado": "active",
          "proximo_pago": `<=${expiryDate.toISOString().split('T')[0]}`
        }],
        sort: [{ fieldName: "proximo_pago", sortOrder: "ascend" }]
      });
      
      return response.data.map(record => ({
        id: record.fieldData.id,
        clientName: record.fieldData.cliente_nombre,
        clientEmail: record.fieldData.cliente_email,
        planName: record.fieldData.plan_nombre,
        status: record.fieldData.estado,
        nextPayment: record.fieldData.proximo_pago,
        amount: parseFloat(record.fieldData.monto_uf) || 0,
        startDate: record.fieldData.fecha_inicio,
        paymentMethod: record.fieldData.medio_pago
      }));
    } catch (error) {
      console.error('Error fetching expiring subscriptions:', error);
      throw error;
    }
  }

  /**
   * Obtener estadísticas de suscripciones
   */
  static async getSubscriptionStats() {
    try {
      const response = await filemakerService.findRecords('subscription_states', {
        sort: [{ fieldName: "fecha_creacion", sortOrder: "descend" }]
      });
      
      const data = response.data.map(record => ({
        status: record.fieldData.estado,
        created_at: record.fieldData.fecha_creacion,
        nextPayment: record.fieldData.proximo_pago
      }));

      const now = new Date();
      const stats = {
        total_subscriptions: data.length,
        active_subscriptions: data.filter(s => s.status === 'active').length,
        cancelled_subscriptions: data.filter(s => s.status === 'cancelled').length,
        paused_subscriptions: data.filter(s => s.status === 'paused').length,
        expiring_soon: data.filter(s => {
          if (!s.nextPayment || s.status !== 'active') return false;
          const nextPaymentDate = new Date(s.nextPayment);
          const weekFromNow = new Date();
          weekFromNow.setDate(weekFromNow.getDate() + 7);
          return nextPaymentDate <= weekFromNow && nextPaymentDate > now;
        }).length
      };

      return stats;
    } catch (error) {
      console.error('Error fetching subscription stats:', error);
      throw error;
    }
  }

  /**
   * Obtener historial de suscripciones de un usuario
   */
  static async getUserSubscriptionHistory(clientEmail) {
    try {
      const response = await filemakerService.findRecords('subscription_states', {
        query: [{
          "cliente_email": clientEmail
        }],
        sort: [{ fieldName: "fecha_creacion", sortOrder: "descend" }]
      });
      
      return response.data.map(record => ({
        id: record.fieldData.id,
        clientName: record.fieldData.cliente_nombre,
        clientEmail: record.fieldData.cliente_email,
        planName: record.fieldData.plan_nombre,
        status: record.fieldData.estado,
        nextPayment: record.fieldData.proximo_pago,
        amount: parseFloat(record.fieldData.monto_uf) || 0,
        startDate: record.fieldData.fecha_inicio,
        paymentMethod: record.fieldData.medio_pago,
        periodicidad: record.fieldData.periodicidad,
        created_at: record.fieldData.fecha_creacion,
        updated_at: record.fieldData.fecha_modificacion
      }));
    } catch (error) {
      console.error('Error fetching user subscription history:', error);
      throw error;
    }
  }
}

export default SubscriptionsService;